import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { zodResolver } from '@hookform/resolvers/zod';

import { CHANNEL_SHORT_NAME_MAP, IChannel, PROTOCOL } from '@/entities/channel';
import { useAuthorisationTgBot } from '@/features/channels/model/useAuthorisationTgBot';
import { Dialog, TDialogBase } from '@/shared/ui/Dialog';
import { Button } from '@/shared/ui/lib/Button';
import { ControlledTextField } from '@/shared/ui/TextField';

import { createTgBotFormDataSchema } from '../model/schemas';
import { TModeTgBotDialog } from '../model/types';

import { ChannelIconWithLabel } from './ChannelIconWithLabel';

const FORM_ID = 'create-tg-bot-form';
interface ICreateTgBotDialogType extends TDialogBase {
  onCreated?: (channel: IChannel) => void;
  channelId: string;
  modeDialog?: TModeTgBotDialog;
}

export const CreateTgBotDialog = ({
  open,
  onClose,
  onCreated,
  channelId,
  modeDialog = 'create',
}: ICreateTgBotDialogType) => {
  const { t } = useTranslation();

  const {
    control,
    formState: { errors },
    handleSubmit,
    reset: resetFormValues,
  } = useForm({
    resolver: zodResolver(createTgBotFormDataSchema(t)),
    defaultValues: {
      token: '',
    },
    mode: 'onChange',
  });

  const { authorizeTgBot, isPending } = useAuthorisationTgBot();

  const handleCreate = async (channelData: IChannel) => {
    resetFormValues();
    onCreated?.(channelData);
  };

  const onSubmit = handleSubmit(async (data) => {
    await authorizeTgBot(channelId, data, handleCreate);
  });

  const handleClose = () => {
    onClose?.();
    resetFormValues();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      title={
        modeDialog === 'create'
          ? t('channels:dialogTitle')
          : t('channels:settings.changeTgBotDialog.changeTgBot')
      }
      additionalTitleSlot={
        <ChannelIconWithLabel
          label={CHANNEL_SHORT_NAME_MAP[PROTOCOL.TELEGRAM_BOT]}
          protocol={PROTOCOL.TELEGRAM_BOT}
        />
      }
      leftButtonSlot={
        <Button variant="outlined" onClick={handleClose}>
          {t('channels:buttons.closeModal')}
        </Button>
      }
      rightButtonSlot={
        <Button
          variant="contained"
          type="submit"
          form={FORM_ID}
          loading={isPending}
        >
          {modeDialog === 'create'
            ? t('channels:tgBot.create')
            : t('common:buttons.save')}
        </Button>
      }
    >
      <form onSubmit={onSubmit} id={FORM_ID}>
        <ControlledTextField
          name="token"
          control={control}
          autoFocus
          error={!!errors.token}
          label={t('channels:tgBot.tokenLabelInput')}
        />
      </form>
    </Dialog>
  );
};
